import React from 'react';

import {
  getWorkspaceMe,
  invalidateWorkspaceQuery,
  invalidateWorkspacesForUserQuery,
} from '@repo/api-client';
import { useTranslation } from '@repo/i18n';
import { subscriptionApi } from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { Button } from '@repo/ui/components/Buttons';
import { fireToast } from '@repo/ui/components/Toast';
import { P3, P4 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import useFreeTrial from '../../../../hooks/useFreeTrial';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../../stores/planSelectionStore';
import { useUserStore } from '../../../../stores/userStore';
import { actionRetry } from '../../../../utils/actionRetry';
import { usePaymentFormHandlers, usePaymentStatus } from '../../../Payment';
import useBillingPrices from '../../hooks/useBillingPrices';

import { FREE_TRIAL_NOTE } from './constants';
import { ProceedButtonWrapper } from './styles';
import { PaySubscriptionButtonProps } from './types';

const getReturnUrl = (
  returnUrlParams: PaySubscriptionButtonProps['returnUrlParams'] = {}
): string => {
  const url = new URL(window.location.href);
  Object.entries(returnUrlParams).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  return url.toString();
};

const updateSubscription = (): Promise<void> => {
  const currentSubscription = useSubscriptionStore.getState().subscription;
  const currentPlan = currentSubscription?.plan?.name;
  const wasPastDue = currentSubscription?.pastDue;

  return new Promise((resolve, reject) => {
    // We're polling here because some time needs to pass
    // before Stripe calls our API and the subscription is updated.
    setTimeout(async () => {
      const { success } = await actionRetry(
        async () => {
          await useSubscriptionStore.getState().loadSubscription();
        },
        () =>
          useSubscriptionStore.getState().subscription?.plan?.name !==
            currentPlan ||
          (wasPastDue && useSubscriptionStore.getState().subscription?.active)
            ? 'done'
            : 'retry',
        {
          delay: 1500,
          attempts: 3,
        }
      );

      if (!success) {
        console.error('Exceeded allowed number of tries');
        reject('Failed to update subscription. Please, try again later.');
      } else {
        await useUserStore.getState().updateUserInfo();
        const workspaceResponse = await getWorkspaceMe();
        // This is needed for the website.
        // TODO create a proper solution by https://app.clickup.com/t/86c2ztc52
        invalidateWorkspacesForUserQuery();
        if (workspaceResponse.success) {
          invalidateWorkspaceQuery(workspaceResponse.workspace.id);
        }
        resolve();
      }
    }, 1000);
  });
};

const PaySubscriptionButton: React.FC<PaySubscriptionButtonProps> = ({
  disabled,
  onSuccess,
  returnUrlParams,
  label,
  pastDue,
  nextChargeDate,
  dataTestId,
}) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = React.useState(false);
  const [isCallingApi, setIsCallingApi] = React.useState(false);
  const promoCode = usePlanSelectionStore(planSelectionStoreSelector.promoCode);

  const freeTrial = useFreeTrial();
  const getPastDuePaymentIntent = useSubscriptionStore(
    subscriptionStoreSelector.getPastDuePaymentIntent
  );
  const { validateForm, updateBillingInfo, confirmIntent } =
    usePaymentFormHandlers({
      service: 'Subscription',
    });
  const paymentStatus = usePaymentStatus();
  const pricesToChange = useBillingPrices();

  if (!pricesToChange) {
    return null;
  }

  const handlePastDue = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const intent = await getPastDuePaymentIntent();

    if (!intent?.success && 'error' in intent) {
      return { success: false, error: intent?.error };
    }

    const confirmedIntent = await confirmIntent({
      intentId: intent.paymentIntent.id,
      paymentIntentClientSecret: intent.paymentIntent.clientSecret,
      returnUrl: getReturnUrl(returnUrlParams),
    });

    return { success: !!confirmedIntent?.success };
  };

  const handleSubscription = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const returnUrl = getReturnUrl(returnUrlParams);

    const subscription = await subscriptionApi.createSubscription({
      localPrices: pricesToChange,
      promoCode: promoCode?.code,
    });

    if (!subscription?.success) {
      return subscription;
    }

    let confirmedIntent;

    if (subscription.setupIntent) {
      confirmedIntent = await confirmIntent({
        returnUrl,
        intentId: subscription.setupIntent.id,
        setupIntentClientSecret: subscription.setupIntent.clientSecret,
      });
    } else if (subscription.paymentIntent) {
      confirmedIntent = await confirmIntent({
        returnUrl,
        intentId: subscription.paymentIntent.id,
        paymentIntentClientSecret: subscription.paymentIntent.clientSecret,
      });
    }

    return { success: !!confirmedIntent?.success };
  };

  const handlePayment = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const isFormValid = await validateForm();

      if (!isFormValid) {
        return;
      }

      await updateBillingInfo();

      const result = pastDue
        ? await handlePastDue()
        : await handleSubscription();

      if (!result?.success) {
        console.error(result);
        setIsCallingApi(false);
        throw new Error(result.error);
      }

      setIsCallingApi(true);

      await updateSubscription()
        .then(onSuccess)
        .catch((message) => {
          fireToast({
            error: true,
            label: message,
            duration: 5000,
          });
        })
        .finally(() => setIsCallingApi(false));
    } catch (e) {
      const reason = e instanceof Error ? e.message : e;
      const message = `Failed to complete payment - ${
        reason || 'Something went wrong'
      }`;

      console.error(message);

      fireToast({
        error: true,
        label: message,
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const defaultLabel = isCallingApi
    ? t('subscriptionModal:pay_subscription_button.update_subscription')
    : freeTrial.isAvailable
      ? t('subscriptionModal:pay_subscription_button.start_free_trial')
      : t('subscriptionModal:pay_subscription_button.upgrade_now');

  return (
    <ProceedButtonWrapper>
      <Button
        dataTestId={dataTestId}
        disabled={disabled}
        label={label ?? defaultLabel}
        loading={paymentStatus.loading || isCallingApi || isLoading}
        name="subscriptionPaymentButton"
        width="100%"
        onClick={handlePayment}
      />
      {freeTrial.isAvailable && !isCallingApi ? (
        nextChargeDate ? (
          <P4 color={themeStyle.varInkMedium}>
            {t('subscriptionModal:free_trial.hold_note')}
          </P4>
        ) : (
          <P3>{FREE_TRIAL_NOTE}</P3>
        )
      ) : null}
    </ProceedButtonWrapper>
  );
};

export default PaySubscriptionButton;
